import React, { useState } from 'react';
import { Phone, MapPin, Clock, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
    isUrgent: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation basique
    if (!formData.name || !formData.phone || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Simulation d'envoi (frontend uniquement)
    toast.success('Votre demande a été envoyée ! Nous vous recontacterons rapidement.');
    
    // Réinitialiser le formulaire
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      message: '',
      isUrgent: false
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Header */}
      <section className="py-12 bg-gradient-to-br from-[#0B2A4A]/5 via-white to-[#2F80ED]/5">
        <div className="container mx-auto px-4">
          <Badge className="bg-[#2F80ED] text-white mb-4">Contact</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
            Contactez-nous
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
            Une question ? Un projet ? Une urgence ? Nous sommes à votre écoute. 
            Contactez-nous par téléphone ou remplissez le formulaire ci-dessous.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-[#0B2A4A] mb-8">
                Contact rapide
              </h2>

              {/* Phone Card - Priority */}
              <Card className="mb-6 border-[#2F80ED] border-2 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#2F80ED] rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0B2A4A]">Téléphone</h3>
                      <p className="text-sm text-gray-600">Appelez-nous maintenant</p>
                    </div>
                  </div>
                  <a 
                    href={`tel:${companyInfo.phoneFormatted}`}
                    className="text-2xl font-bold text-[#2F80ED] hover:text-[#0B2A4A] transition-colors block mb-4"
                  >
                    {companyInfo.phone}
                  </a>
                  <a href={`tel:${companyInfo.phoneFormatted}`}>
                    <Button className="w-full bg-[#2F80ED] hover:bg-[#0B2A4A] gap-2 transition-all duration-300">
                      <Phone className="w-4 h-4" />
                      Appeler maintenant
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Other Contact Info */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#2F80ED]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0B2A4A] mb-2">Email</h3>
                      <a 
                        href={`mailto:${companyInfo.email}`}
                        className="text-gray-600 hover:text-[#2F80ED] transition-colors"
                      >
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#2F80ED]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0B2A4A] mb-2">Adresse</h3>
                      <p className="text-gray-600">{companyInfo.address}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Zone d'intervention : {companyInfo.zone} (rayon {companyInfo.serviceRadius})
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#2F80ED]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0B2A4A] mb-3">Horaires</h3>
                      <div className="space-y-2 text-sm">
                        <p className="text-gray-600">{companyInfo.hours.weekdays}</p>
                        <p className="text-gray-600">{companyInfo.hours.saturday}</p>
                        <p className="text-gray-600">{companyInfo.hours.sunday}</p>
                        <p className="text-[#2F80ED] font-semibold pt-2 border-t border-gray-200">
                          {companyInfo.hours.emergency}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-[#0B2A4A] mb-8">
                Demander un devis
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#0B2A4A]">
                    Nom complet <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-[#0B2A4A]">
                      Téléphone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#0B2A4A]">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jean@exemple.fr"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address" className="text-[#0B2A4A]">Adresse d'intervention</Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder="15 Rue de la République, 69007 Lyon"
                    value={formData.address}
                    onChange={handleChange}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#0B2A4A]">
                    Description de votre demande <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Décrivez votre problème ou projet de plomberie..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="mt-2"
                  />
                </div>

                <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg border border-red-200">
                  <input
                    id="isUrgent"
                    name="isUrgent"
                    type="checkbox"
                    checked={formData.isUrgent}
                    onChange={handleChange}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <Label htmlFor="isUrgent" className="text-red-700 font-semibold cursor-pointer">
                    C'est une urgence (intervention rapide souhaitée)
                  </Label>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-[#2F80ED] hover:bg-[#0B2A4A] gap-2 transition-all duration-300"
                >
                  <Send className="w-5 h-5" />
                  Envoyer la demande
                </Button>

                <p className="text-sm text-gray-600 text-center">
                  <CheckCircle className="w-4 h-4 inline mr-1 text-[#2F80ED]" />
                  Réponse sous 2h en moyenne
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-[#0B2A4A] mb-8 text-center">
            Notre localisation
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-300 rounded-2xl overflow-hidden aspect-video flex items-center justify-center shadow-lg">
              <div className="text-center p-8">
                <MapPin className="w-16 h-16 text-[#2F80ED] mx-auto mb-4" />
                <p className="text-xl font-bold text-[#0B2A4A] mb-2">{companyInfo.address}</p>
                <p className="text-gray-600 mb-4">Lyon & Grand Lyon (rayon {companyInfo.serviceRadius})</p>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-[#2F80ED] text-[#2F80ED] hover:bg-[#2F80ED] hover:text-white">
                    Ouvrir dans Google Maps
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
